import React, { useCallback } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  PasswordInput,
  SaveButton,
  Toolbar,
  ReferenceArrayInput,
  SelectArrayInput,
  ChipField,
  useUpdate,
  useRedirect,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from "@material-ui/icons/Info";
import { makeStyles } from "@material-ui/core/styles";
import customTheme from "../../customTheme";
import { PASSWORD_PATTERN, PASSWORD_HINT } from "./change-password";
import { useCanManageUserPassword } from "./userRoleUtils";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
  },
  sectionTitle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  infoIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
  },
  infoIcon: {
    fontSize: 16,
  },
  passwordPanel: {
    paddingLeft: 24,
    borderLeft: "1px solid #e0e0e0",
    width: "100%",
  },
}));

const validatePasswordStrength = (value) => {
  if (!value) return undefined;
  if (!PASSWORD_PATTERN.test(value)) return PASSWORD_HINT;
  return undefined;
};

const validateConfirmPassword = (value, allValues) => {
  if (!allValues.password && !value) return undefined;
  if (allValues.password && !value) return "Please confirm your password";
  if (value !== allValues.password) return "The passwords do not match";
  return undefined;
};

const CustomSave = ({ handleSubmitWithRedirect, ...props }) => {
  const [update] = useUpdate("users", props.record.id);
  const redirectTo = useRedirect();
  const notify = useNotify();
  const form = useForm();
  const { basePath } = props;

  const handleClick = useCallback(() => {
    const { created_at, id, confirmPassword, password, ...rest } =
      form.getState().values;

    const data = { ...rest };

    if (password) {
      data.password = password;
    }

    update(
      {
        payload: { data },
      },
      {
        onSuccess: ({ data: responseData }) => {
          notify(responseData.message, 2000);
          redirectTo(basePath);
        },
      }
    );
  }, [form, update, notify, redirectTo, basePath]);

  return <SaveButton {...props} handleSubmitWithRedirect={handleClick} />;
};

const EditToolbar = (props) => (
  <Toolbar {...props}>
    <CustomSave redirect="list" submitOnEnter={false} variant="text" />
  </Toolbar>
);

const UserEditContent = (props) => {
  const classes = useStyles();
  const { canManage: showPasswordSection } = useCanManageUserPassword();

  return (
    <SimpleForm {...props} toolbar={<EditToolbar />}>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12} md={showPasswordSection ? 6 : 12}>
          <TextInput source="name" fullWidth />
          <TextInput source="email" fullWidth />
          <ReferenceArrayInput label="Roles" reference="roles" source="roles">
            <SelectArrayInput>
              <ChipField source="name" />
            </SelectArrayInput>
          </ReferenceArrayInput>
        </Grid>

        {showPasswordSection && (
          <Grid item xs={12} md={6}>
            <div className={classes.passwordPanel}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Change Password
                <Tooltip title={PASSWORD_HINT} arrow leaveDelay={200}>
                  <span className={classes.infoIconWrap}>
                    <InfoIcon
                      htmlColor={customTheme.palette.secondary.dark}
                      className={classes.infoIcon}
                    />
                  </span>
                </Tooltip>
              </Typography>
              <PasswordInput
                source="password"
                label="New Password"
                validate={validatePasswordStrength}
                fullWidth
                defaultValue=""
              />
              <PasswordInput
                source="confirmPassword"
                label="Confirm Password"
                validate={validateConfirmPassword}
                fullWidth
                defaultValue=""
              />
            </div>
          </Grid>
        )}
      </Grid>
    </SimpleForm>
  );
};

const UserEdit = (props) => {
  return (
    <Edit {...props}>
      <UserEditContent />
    </Edit>
  );
};

export default UserEdit;
